import React, { useState, useEffect, useCallback } from 'react';
import { officesAPI } from '../lib/api';
import api from '../lib/api';
import { useCap } from '../components/auth/RequireCap';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  Plus, Search, Building, Pencil, Eye, Upload, Download, FileSpreadsheet,
  History, AlertTriangle, MapPin, Link2, X, Trash2,
} from 'lucide-react';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];
const TYPE_OPTIONS = [
  { value: 'headquarters', label: 'Headquarters' },
  { value: 'regional_office', label: 'Regional Office' },
  { value: 'operations_center', label: 'Operations Center' },
  { value: 'home_office', label: 'Home Office' },
];
const typeLabel = (v) => (TYPE_OPTIONS.find(t => t.value === v) || {}).label || v;

const emptyOffice = {
  name: '', office_type: 'regional_office', parent_site_id: null, street: '', suite: '',
  city: '', state: '', zip_code: '', mailing_address: '', mailing_city: '', mailing_state: '',
  mailing_zip: '', phone: '', office_manager_employee_id: null, region: '', ledger_branch_id: '',
  active: true, notes: '',
};

// A minimal async search combobox used for manager / parent-site / assignment pickers.
function EntitySearch({ placeholder, searchFn, onPick, testid }) {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!term || term.length < 2) { setResults([]); return; }
    let live = true;
    const t = setTimeout(async () => {
      try { const r = await searchFn(term); if (live) { setResults(r); setOpen(true); } } catch { /* ignore */ }
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [term, searchFn]);
  return (
    <div className="relative">
      <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder={placeholder} data-testid={testid} />
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md">
          {results.map(r => (
            <button key={r.id} type="button" className="block w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
              onClick={() => { onPick(r); setTerm(''); setResults([]); setOpen(false); }}
              data-testid={`${testid}-opt-${r.id}`}>
              {r.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const searchEmployees = async (q) => {
  const { data } = await api.get('/employees', { params: { page: 1, page_size: 8, q } });
  return (data.items || []).map(e => ({ id: e.id, label: `${e.first_name || ''} ${e.last_name || ''}`.trim() + (e.employee_number ? ` (#${e.employee_number})` : '') }));
};
// Parent of an office is ANOTHER OFFICE (the campus it belongs under). excludeId
// keeps an office out of its own parent picker.
const makeSearchOffices = (excludeId) => async (q) => {
  const { data } = await officesAPI.list({ q });
  return (data.offices || [])
    .filter(o => o.id !== excludeId)
    .slice(0, 8)
    .map(o => ({ id: o.id, label: o.name }));
};

export default function OfficeLocations() {
  const canEdit = useCap('places_offices', 'edit');
  const canAdd = useCap('places_offices', 'add');
  const canDelete = useCap('places_offices', 'delete');
  const [rows, setRows] = useState([]);
  const [types] = useState(TYPE_OPTIONS);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (typeFilter !== 'all') params.office_type = typeFilter;
      params.include_inactive = showInactive;
      const { data } = await officesAPI.list(params);
      setRows(data.offices || []);
    } catch (e) {
      toast.error(e.userMessage || 'Failed to load offices');
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter, showInactive]);

  useEffect(() => { load(); }, [load]);

  const onTemplate = async () => {
    try {
      const { data } = await officesAPI.template();
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a'); a.href = url; a.download = 'offices_template.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Template download failed'); }
  };
  const onExport = async () => {
    try {
      const { data } = await officesAPI.export();
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a'); a.href = url; a.download = 'offices.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="office-locations-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Building className="w-6 h-6" /> Office Locations
          </h1>
          <p className="text-sm text-slate-500">The company's offices — headquarters, regional offices, operations centers and home offices.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={onTemplate} data-testid="offices-template-btn"><FileSpreadsheet className="w-4 h-4 mr-2" />Template</Button>
          <Button variant="outline" onClick={onExport} data-testid="offices-export-btn"><Download className="w-4 h-4 mr-2" />Export</Button>
          {canEdit && <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="offices-import-btn"><Upload className="w-4 h-4 mr-2" />Import</Button>}
          {canAdd && <Button onClick={() => setEditing({ ...emptyOffice })} data-testid="add-office-btn"><Plus className="w-4 h-4 mr-2" />Add Office</Button>}
        </div>
      </div>

      <Card>
        <CardContent className="p-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Search name, city, state, region" value={search}
              onChange={(e) => setSearch(e.target.value)} data-testid="offices-search" />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[180px]" data-testid="offices-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {types.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="offices-show-inactive" />
            <Label className="text-sm">Show inactive</Label>
          </div>
          <Badge variant="secondary" data-testid="offices-count">{rows.length} office{rows.length === 1 ? '' : 's'}</Badge>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Manager</TableHead>
                <TableHead>Region</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Loading…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">No offices yet.</TableCell></TableRow>
              ) : rows.map(o => (
                <TableRow key={o.id} data-testid={`office-row-${o.id}`}>
                  <TableCell className="font-medium">{o.name}</TableCell>
                  <TableCell>{typeLabel(o.office_type)}</TableCell>
                  <TableCell className="text-sm text-slate-600">
                    {[o.city, o.state].filter(Boolean).join(', ') || '—'}
                  </TableCell>
                  <TableCell className="text-sm">{o.office_manager_name || '—'}</TableCell>
                  <TableCell className="text-sm">{o.region || '—'}</TableCell>
                  <TableCell>{o.active ? <Badge>Active</Badge> : <Badge variant="secondary">Inactive</Badge>}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="icon" variant="ghost" onClick={() => setDetail(o)} data-testid={`office-view-${o.id}`}><Eye className="w-4 h-4" /></Button>
                    {canEdit && <Button size="icon" variant="ghost" onClick={() => setEditing(o)} data-testid={`office-edit-${o.id}`}><Pencil className="w-4 h-4" /></Button>}
                    {canDelete && <Button size="icon" variant="ghost" onClick={() => setDeleting(o)} data-testid={`office-delete-${o.id}`} title="Delete office"><Trash2 className="w-4 h-4 text-red-600" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {editing && <OfficeEditor office={editing} canDelete={canDelete} onDelete={(o) => { setEditing(null); setDeleting(o); }} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {detail && <OfficeDetail office={detail} canEdit={canEdit} onClose={() => setDetail(null)} onChanged={load} />}
      {deleting && <DeleteOfficeDialog office={deleting} onClose={() => setDeleting(null)} onDeleted={() => { setDeleting(null); load(); }} />}
      {importOpen && <OfficeImport open={importOpen} onOpenChange={setImportOpen} onDone={load} />}
    </div>
  );
}

function OfficeEditor({ office, canDelete, onDelete, onClose, onSaved }) {
  const isNew = !office.id;
  const [form, setForm] = useState({ ...emptyOffice, ...office });
  const [mgrName, setMgrName] = useState(office.office_manager_name || '');
  const [siteName, setSiteName] = useState(office.parent_site_name || '');
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const searchOffices = makeSearchOffices(office.id);

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setBusy(true);
    try {
      if (isNew) { await officesAPI.create(form); toast.success('Office added'); }
      else { await officesAPI.update(office.id, form); toast.success('Office updated'); }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.userMessage || 'Save failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="office-editor-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Add Office' : 'Edit Office'}</DialogTitle>
          <DialogDescription>Delete an office only if nothing uses it. Otherwise mark it inactive.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="col-span-2"><Label>Name *</Label><Input value={form.name} onChange={e => set('name', e.target.value)} data-testid="office-name-input" /></div>
          <div>
            <Label>Type *</Label>
            <Select value={form.office_type} onValueChange={v => set('office_type', v)}>
              <SelectTrigger data-testid="office-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPE_OPTIONS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Parent office (campus)</Label>
            {form.parent_site_id ? (
              <div className="flex items-center gap-2 text-sm mt-2">
                <Badge variant="secondary">{siteName || 'Selected'}</Badge>
                <Button size="icon" variant="ghost" onClick={() => { set('parent_site_id', null); setSiteName(''); }}><X className="w-3 h-3" /></Button>
              </div>
            ) : (
              <EntitySearch placeholder="Search another office…" searchFn={searchOffices}
                onPick={(r) => { set('parent_site_id', r.id); setSiteName(r.label); }} testid="office-parent-search" />
            )}
          </div>
          <div className="col-span-2 border-t pt-2 text-xs font-semibold text-slate-500">PHYSICAL ADDRESS</div>
          <div><Label>Street</Label><Input value={form.street || ''} onChange={e => set('street', e.target.value)} data-testid="office-street-input" /></div>
          <div><Label>Suite / Unit</Label><Input value={form.suite || ''} onChange={e => set('suite', e.target.value)} data-testid="office-suite-input" /></div>
          <div><Label>City</Label><Input value={form.city || ''} onChange={e => set('city', e.target.value)} data-testid="office-city-input" /></div>
          <div>
            <Label>State</Label>
            <Select value={form.state || ''} onValueChange={v => set('state', v)}>
              <SelectTrigger data-testid="office-state-select"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>ZIP</Label><Input value={form.zip_code || ''} onChange={e => set('zip_code', e.target.value)} data-testid="office-zip-input" /></div>
          <div className="col-span-2 border-t pt-2 text-xs font-semibold text-slate-500">MAILING ADDRESS (e.g. a PO Box)</div>
          <div className="col-span-2"><Label>Mailing address</Label><Input value={form.mailing_address || ''} onChange={e => set('mailing_address', e.target.value)} placeholder="PO Box 1234" data-testid="office-mailing-input" /></div>
          <div><Label>Mailing city</Label><Input value={form.mailing_city || ''} onChange={e => set('mailing_city', e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Mailing state</Label><Input value={form.mailing_state || ''} onChange={e => set('mailing_state', e.target.value.toUpperCase().slice(0,2))} /></div>
            <div><Label>Mailing ZIP</Label><Input value={form.mailing_zip || ''} onChange={e => set('mailing_zip', e.target.value)} /></div>
          </div>
          <div className="col-span-2 border-t pt-2 text-xs font-semibold text-slate-500">CONTACT & MANAGEMENT</div>
          <div><Label>Phone</Label><Input value={form.phone || ''} onChange={e => set('phone', e.target.value)} data-testid="office-phone-input" /></div>
          <div><Label>Region</Label><Input value={form.region || ''} onChange={e => set('region', e.target.value)} data-testid="office-region-input" /></div>
          <div>
            <Label>Office manager</Label>
            {form.office_manager_employee_id ? (
              <div className="flex items-center gap-2 text-sm mt-2">
                <Badge variant="secondary">{mgrName || 'Selected'}</Badge>
                <Button size="icon" variant="ghost" onClick={() => { set('office_manager_employee_id', null); setMgrName(''); }}><X className="w-3 h-3" /></Button>
              </div>
            ) : (
              <EntitySearch placeholder="Search an employee…" searchFn={searchEmployees}
                onPick={(r) => { set('office_manager_employee_id', r.id); setMgrName(r.label); }} testid="office-manager-search" />
            )}
          </div>
          <div><Label>Ledger branch ID</Label><Input value={form.ledger_branch_id || ''} onChange={e => set('ledger_branch_id', e.target.value)} data-testid="office-branch-input" /></div>
          <div className="col-span-2 flex items-center gap-3">
            <Switch checked={!!form.active} onCheckedChange={v => set('active', v)} data-testid="office-active-switch" />
            <Label>Active</Label>
          </div>
          <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} data-testid="office-notes-input" /></div>
        </div>
        <DialogFooter>
          {!isNew && canDelete && (
            <Button variant="outline" className="mr-auto text-red-600 hover:text-red-700" onClick={() => onDelete(office)} data-testid="office-editor-delete-btn">
              <Trash2 className="w-4 h-4 mr-2" />Delete
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="save-office-btn">{busy ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OfficeDetail({ office, canEdit, onClose, onChanged }) {
  const [history, setHistory] = useState([]);
  const [links, setLinks] = useState({ employees: [], jobs: [], licenses: [] });
  const [active, setActive] = useState(office.active !== false);
  const [tab, setTab] = useState('info');
  const reload = useCallback(async () => {
    const [h, l] = await Promise.all([officesAPI.history(office.id), officesAPI.links(office.id)]);
    setHistory(h.data || []); setLinks(l.data || { employees: [], jobs: [], licenses: [] });
  }, [office.id]);
  useEffect(() => { reload(); }, [reload]);

  const deactivate = async () => {
    try { const { data } = await officesAPI.deactivate(office.id); setActive(data.active); toast.success(data.active ? 'Office reactivated' : 'Office deactivated'); onChanged(); reload(); }
    catch (e) { toast.error(e.userMessage || 'Failed'); }
  };
  const assignEmployees = async (ids) => {
    try { await officesAPI.assign(office.id, { employee_ids: ids }); toast.success('Linked'); reload(); }
    catch (e) { toast.error(e.userMessage || 'Assign failed'); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="office-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MapPin className="w-5 h-5" />{office.name}</DialogTitle>
          <DialogDescription>{typeLabel(office.office_type)}{active ? '' : ' · Inactive'}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 border-b">
          {['info', 'links', 'history'].map(t => (
            <button key={t} className={`px-3 py-2 text-sm ${tab === t ? 'border-b-2 border-slate-900 font-medium' : 'text-slate-500'}`}
              onClick={() => setTab(t)} data-testid={`office-tab-${t}`}>{t[0].toUpperCase() + t.slice(1)}</button>
          ))}
        </div>

        {tab === 'info' && (
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 py-3 text-sm">
            <Field label="Physical address" value={[office.street, office.suite, [office.city, office.state].filter(Boolean).join(', '), office.zip_code].filter(Boolean).join(', ')} />
            <Field label="Mailing address" value={[office.mailing_address, [office.mailing_city, office.mailing_state].filter(Boolean).join(', '), office.mailing_zip].filter(Boolean).join(', ')} />
            <Field label="Phone" value={office.phone} />
            <Field label="Region" value={office.region} />
            <Field label="Office manager" value={office.office_manager_name} />
            <Field label="Parent office" value={office.parent_site_name} />
            <Field label="Ledger branch ID" value={office.ledger_branch_id} />
            <Field label="Notes" value={office.notes} />
          </div>
        )}

        {tab === 'links' && (
          <div className="py-3 space-y-4 text-sm" data-testid="office-links">
            <LinkList title="Employees (home office)" items={links.employees.map(e => ({ id: e.id, label: `${e.first_name || ''} ${e.last_name || ''}`.trim() + (e.employee_number ? ` (#${e.employee_number})` : '') }))} />
            <LinkList title="Jobs" items={links.jobs.map(j => ({ id: j.id, label: j.name || j.job_number || j.id }))} />
            <LinkList title="Company licenses" items={links.licenses.map(l => ({ id: l.id, label: `${l.license_type || ''} · ${l.state || ''} · ${l.license_number || ''}` }))} />
            {canEdit && (
              <div className="border-t pt-3">
                <Label className="flex items-center gap-1"><Link2 className="w-4 h-4" /> Assign an employee's home office</Label>
                <p className="text-xs text-slate-500 mb-2">Nothing changes until you pick someone — assignment is always manual.</p>
                <EntitySearch placeholder="Search an employee to link…" searchFn={searchEmployees}
                  onPick={(r) => assignEmployees([r.id])} testid="office-assign-employee-search" />
              </div>
            )}
          </div>
        )}

        {tab === 'history' && (
          <div className="py-3 space-y-2 text-sm max-h-72 overflow-y-auto" data-testid="office-history">
            {history.length === 0 ? <p className="text-slate-400">No history.</p> : history.map((h, i) => (
              <div key={i} className="flex items-start gap-2 border-b pb-2">
                <History className="w-4 h-4 mt-0.5 text-slate-400" />
                <div>
                  <div className="font-medium">{h.action}{h.by_name ? ` · ${h.by_name}` : ''}</div>
                  <div className="text-xs text-slate-500">{h.at ? new Date(h.at).toLocaleString() : ''}</div>
                  {h.changes && h.changes.length > 0 && (
                    <ul className="text-xs text-slate-500 list-disc ml-4">
                      {h.changes.map((c, j) => <li key={j}>{c.field}: {String(c.from ?? '—')} → {String(c.to ?? '—')}</li>)}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          {canEdit && <Button variant="outline" onClick={deactivate} data-testid="office-deactivate-btn">{active ? 'Deactivate' : 'Reactivate'}</Button>}
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, value }) => (
  <div><div className="text-xs text-slate-400">{label}</div><div>{value || '—'}</div></div>
);
const LinkList = ({ title, items }) => (
  <div>
    <div className="text-xs font-semibold text-slate-500">{title} ({items.length})</div>
    {items.length === 0 ? <div className="text-slate-400">None linked</div> : (
      <ul className="list-disc ml-5">{items.map(i => <li key={i.id}>{i.label}</li>)}</ul>
    )}
  </div>
);

function DeleteOfficeDialog({ office, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [linkInfo, setLinkInfo] = useState(null);
  const address = [office.street, office.suite, [office.city, office.state].filter(Boolean).join(', '), office.zip_code]
    .filter(Boolean).join(', ') || 'No address on file';

  const doDelete = async () => {
    setBusy(true);
    try {
      await officesAPI.remove(office.id);
      toast.success(`Deleted ${office.name}`);
      onDeleted();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail && typeof detail === 'object') {
        setLinkInfo(detail);
      } else {
        toast.error((typeof detail === 'string' && detail) || e.userMessage || 'Delete failed');
      }
    } finally { setBusy(false); }
  };

  const markInactive = async () => {
    setBusy(true);
    try {
      await officesAPI.deactivate(office.id);
      toast.success(`${office.name} marked inactive`);
      onDeleted();
    } catch (e) {
      toast.error(e.userMessage || 'Could not mark inactive');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="office-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="w-5 h-5" /> Delete office?
          </DialogTitle>
          <DialogDescription>{linkInfo ? '' : "This can't be undone."}</DialogDescription>
        </DialogHeader>
        <div className="py-2 text-sm">
          <div className="rounded-md border p-3">
            <div className="font-semibold" data-testid="office-delete-name">{office.name}</div>
            <div className="text-muted-foreground" data-testid="office-delete-address">{address}</div>
          </div>
          {linkInfo && (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-800" data-testid="office-delete-linked">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{linkInfo.message}</span>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="office-delete-cancel-btn">Cancel</Button>
          {linkInfo ? (
            <Button onClick={markInactive} disabled={busy} data-testid="office-delete-deactivate-btn">
              {busy ? 'Working…' : 'Mark inactive instead'}
            </Button>
          ) : (
            <Button variant="destructive" onClick={doDelete} disabled={busy} data-testid="office-delete-confirm-btn">
              {busy ? 'Deleting…' : 'Delete office'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OfficeImport({ open, onOpenChange, onDone }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [importError, setImportError] = useState('');
  const reset = () => { setPreview(null); setImportError(''); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setImportError('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await officesAPI.importPreview(fd);
      setPreview(data);
    } catch (err) {
      const msg = err.response?.data?.detail || err.userMessage || 'Could not read the file';
      setImportError(msg); toast.error(msg);
    } finally { setBusy(false); e.target.value = ''; }
  };
  const confirm = async () => {
    setBusy(true); setImportError('');
    try {
      const { data } = await officesAPI.importConfirm({ token: preview.token, skip: [] });
      toast.success(`Imported: ${data.created} new, ${data.updated} updated, ${data.skipped} skipped`);
      reset(); onOpenChange(false); onDone?.();
    } catch (err) {
      const msg = err.response?.data?.detail || err.userMessage || 'Import failed';
      setImportError(msg); toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-3xl" data-testid="office-import-dialog">
        <DialogHeader>
          <DialogTitle>Import Offices</DialogTitle>
          <DialogDescription>
            Upload an Excel file. Required columns: Name, Type. Type must be one of:
            Headquarters, Regional Office, Operations Center, Home Office. Parent Site, if set,
            must be the name of another office.
          </DialogDescription>
        </DialogHeader>
        {importError && (
          <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="office-import-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /><span>{importError}</span>
          </div>
        )}
        {!preview ? (
          <label className="block border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:bg-slate-50">
            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={onFile} data-testid="office-import-file-input" />
            <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
            <p className="text-sm text-slate-600">{busy ? 'Reading…' : 'Click to choose an Excel file'}</p>
          </label>
        ) : (
          <div className="space-y-4">
            <div className="flex gap-4 text-sm" data-testid="office-import-summary">
              <span className="text-emerald-600">{preview.summary.new} new</span>
              <span className="text-amber-600">{preview.summary.update} updates</span>
              <span className="text-red-600">{preview.summary.error} errors</span>
              <span className="text-slate-500">{preview.summary.total} rows</span>
            </div>
            <div className="max-h-[50vh] overflow-y-auto border rounded">
              <Table>
                <TableHeader><TableRow><TableHead>Row</TableHead><TableHead>Action</TableHead><TableHead>Name / Type</TableHead></TableRow></TableHeader>
                <TableBody>
                  {preview.rows.map(r => (
                    <TableRow key={r.row} data-testid={`office-import-row-${r.row}`}>
                      <TableCell>{r.row}</TableCell>
                      <TableCell>
                        <Badge variant={r.action === 'error' ? 'destructive' : 'default'}>{r.action}</Badge>
                        {r.errors?.length > 0 && <div className="text-xs text-red-500 mt-1">{r.errors.join('; ')}</div>}
                        {r.warnings?.length > 0 && <div className="text-xs text-amber-500 mt-1">{r.warnings.join('; ')}</div>}
                      </TableCell>
                      <TableCell className="text-sm">{r.data.name} · {typeLabel(r.data.office_type)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={reset} data-testid="office-import-back-btn">Choose another file</Button>
              <Button onClick={confirm} disabled={busy} data-testid="office-import-confirm-btn">{busy ? 'Importing…' : 'Confirm Import'}</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
